import React from 'react';
import {
  Link2,
  CreditCard,
  Mail,
  Phone,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Settings,
  Zap,
  Lock,
  ArrowUpRight,
  Database,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

/**
 * Integrations Section
 * 
 * Service overview showing all integration statuses.
 * 
 * IMPORTANT: Mailgun and Twilio appear here even when NOT enabled.
 * They show locked/requires add-on state with upgrade CTA.
 * 
 * This is the overview layer - clicking opens the respective setup page.
 */
const IntegrationsSection = ({ agency, agencyId, entitlements, isReadOnly, navigateToSection }) => {
  
  // Integration configurations
  const integrations = [
    {
      id: 'mailgun',
      name: 'Mailgun',
      description: 'Email delivery service',
      icon: Mail,
      category: 'communication',
      entitlementKey: 'mailgun_email',
      setupSection: 'communication',
      setupLabel: 'Open Email Services',
    },
    {
      id: 'twilio',
      name: 'Twilio',
      description: 'SMS and phone services',
      icon: Phone,
      category: 'communication',
      entitlementKey: 'twilio_phone',
      setupSection: 'communication',
      setupLabel: 'Open Phone System',
    },
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Payment processing',
      icon: CreditCard,
      category: 'payments',
      entitlementKey: null, // Always available
      setupSection: 'billing',
      setupLabel: 'Open Billing',
      connected: true, // Simulated as connected
    },
    {
      id: 'google-calendar',
      name: 'Google Calendar',
      description: 'Appointment scheduling',
      icon: Calendar,
      category: 'scheduling',
      entitlementKey: null,
      setupSection: 'communication',
      setupLabel: 'Open Communication Settings',
      connected: true, // Simulated as connected
    },
    {
      id: 'document-storage',
      name: 'Document Storage',
      description: 'Secure file storage',
      icon: Database,
      category: 'storage',
      entitlementKey: null,
      setupSection: null,
      setupLabel: null,
      connected: true,
    },
  ];
  
  // Get integration status
  const getIntegrationStatus = (integration) => {
    // If no entitlement required, check if connected
    if (!integration.entitlementKey) {
      return integration.connected ? 'connected' : 'not_connected';
    }
    
    // Check entitlement
    const mode = entitlements[integration.entitlementKey];
    if (mode === 'full_access') {
      return 'connected'; // Simulated as connected if entitled
    }
    return 'requires_addon';
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-0">
            <CheckCircle size={12} className="mr-1" />
            Connected
          </Badge>
        );
      case 'not_connected':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-0">
            <AlertTriangle size={12} className="mr-1" />
            Not Connected
          </Badge>
        );
      case 'requires_addon':
        return (
          <Badge className="bg-slate-100 text-slate-500 border-0">
            <Lock size={12} className="mr-1" />
            Requires Add-on
          </Badge>
        );
      default:
        return null;
    }
  };
  
  // Stats
  const connectedCount = integrations.filter(i => getIntegrationStatus(i) === 'connected').length;
  const availableCount = integrations.length;
  
  return (
    <div className="space-y-6" data-testid="integrations-section">
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Link2 className="text-blue-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Integrations</h2>
            <p className="text-sm text-slate-500">
              Connect and manage third-party services
            </p>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={16} className="text-emerald-500" />
            <p className="text-xs text-slate-500">Connected</p>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{connectedCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={16} className="text-blue-500" />
            <p className="text-xs text-slate-500">Available</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{availableCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} className="text-amber-500" />
            <p className="text-xs text-slate-500">Requires Upgrade</p>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            {integrations.filter(i => getIntegrationStatus(i) === 'requires_addon').length}
          </p>
        </div>
      </div>
      
      {/* Integrations Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Service</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Entitlement</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Setup</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {integrations.map((integration) => {
              const Icon = integration.icon;
              const status = getIntegrationStatus(integration);
              
              return (
                <tr key={integration.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon size={20} className="text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{integration.name}</p>
                        <p className="text-xs text-slate-500">{integration.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(status)}
                  </td>
                  <td className="px-6 py-4">
                    {status === 'requires_addon' ? (
                      <span className="text-sm text-amber-600">Requires add-on</span>
                    ) : (
                      <span className="text-sm text-slate-500">Included</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {integration.setupSection && status !== 'requires_addon' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700"
                        onClick={() => navigateToSection?.(integration.setupSection)}
                      >
                        <ExternalLink size={14} className="mr-1" />
                        {integration.setupLabel}
                      </Button>
                    ) : status === 'requires_addon' ? (
                      <span className="text-sm text-slate-400">Setup unavailable</span>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {status === 'connected' ? (
                      <Button variant="outline" size="sm" disabled={isReadOnly}>
                        <Settings size={14} className="mr-1" />
                        Configure
                      </Button>
                    ) : status === 'not_connected' ? (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={isReadOnly}>
                        Connect
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={() => navigateToSection?.('billing')}
                      >
                        <ArrowUpRight size={14} className="mr-1" />
                        Upgrade / Add Features
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-semibold text-blue-800 mb-2">Integration Setup</h4>
        <p className="text-sm text-blue-700">
          Click on "Open Email Services" or "Open Phone System" to configure Mailgun or Twilio. 
          If a service requires an add-on, click "Upgrade / Add Features" to enable it in Billing & Membership.
        </p>
      </div>
    </div>
  );
};

export default IntegrationsSection;
